import React, {ReactElement, ReactNode} from 'react';

import {useIsFeatureFlagEnabled} from './featureFlag.utils';

interface Props extends PropsWithChildrenRequired {
  name: string;
}

const FeatureFlag = ({name, children}: Props) => {
  const {isFeatureEnabled, isLoading} = useIsFeatureFlagEnabled(name);

  if (isLoading) {
    return (
      <>{React.Children.map(children, (child) => (isLoadingComponent(child) ? child : null))}</>
    );
  }
  if (!isFeatureEnabled) {
    return (
      <>{React.Children.map(children, (child) => (isFallbackComponent(child) ? child : null))}</>
    );
  }
  return (
    <>
      {React.Children.map(children, (child) => {
        if (isLoadingComponent(child)) {
          return null;
        }
        if (isFallbackComponent(child)) {
          return null;
        }
        return child;
      })}
    </>
  );
};

const isReactComponent = (child: ReactNode): child is ReactElement<PropsWithChildrenOptional> => {
  return (
    React.isValidElement(child) &&
    typeof (child.type as any) === 'function' &&
    (child.type as any) === Loading
  );
};

const isFallbackComponent = (
  child: ReactNode
): child is ReactElement<PropsWithChildrenOptional> => {
  return (
    React.isValidElement(child) &&
    typeof (child.type as any) === 'function' &&
    (child.type as any) === Fallback
  );
};

const isLoadingComponent = (
  child: React.ReactNode
): child is React.ReactElement<PropsWithChildrenRequired> => {
  return isReactComponent(child) && (child as any).type === Loading;
};

const Loading = ({children}: PropsWithChildrenRequired) => <>{children}</>;

const Fallback = ({children}: PropsWithChildrenRequired) => <>{children}</>;

FeatureFlag.Loading = Loading;
FeatureFlag.Fallback = Fallback;

export default FeatureFlag;
